import { useMemo } from 'react';

import type { SecurityFocusWithStateChangeTime } from '@rtkwlf/track-public';
import { SecurityFocusState } from '@rtkwlf/track-public';
import type { TFunction } from 'i18next';
import { Link as RouterLink } from 'react-router-dom';

import { Box, Typography } from '@packages/fenrir/components';
import { Button } from '@packages/fenrir/components';
import { CustomChip } from '@packages/fenrir/components';
import {
  DataGridPro,
  getFilteredGridSelectOperators,
  GridSelectOperator,
  type GridColDef,
  type GridRenderCellParams,
} from '@packages/fenrir/components/datagrid';
import { GridToolbar } from '@packages/fenrir/components/datagrid';
import { Stack } from '@packages/fenrir/components/stack';

import { FormattedDate } from '../../../components/formatted-date';
import { createOptions } from '../../../components/tables/_components/create-options';
import { focusStateToAppearance } from '../../../constants/focus-state-to-appearance';
import { routes } from '../../../constants/routes';
import { useSecurityJourneyTranslations } from '../../../locales/use-security-journey-translations';

/* -------------------------------------------------------------------------------------------------
 * Utilities
 * -----------------------------------------------------------------------------------------------*/
type SecurityFocusRow = {
  id: string;
  name: string;
  state: string;
  detour: boolean;
  dateAdded: Date;
  completedAt: Date | null;
  spidrCount: number;
};

type ParseSecurityFocusesForTableParams = {
  /**
   * securityFocuses to be transformed
   */
  focuses: SecurityFocusWithStateChangeTime[];
};

/**
 * Parses securityFocuses into the type that is required
 * for the securityFocuses table
 *
 * @param params of type `ParseSecurityFocusesForTableParams`
 */
const parseSecurityFocusesForTable = (
  params: ParseSecurityFocusesForTableParams,
): SecurityFocusRow[] => {
  if (params.focuses.length === 0) return [];
  const rows = params.focuses.map((focus): SecurityFocusRow => {
    return {
      id: focus.id,
      name: focus.name,
      state: focus.state,
      detour: focus.detour,
      dateAdded: focus.dateAdded,
      completedAt: focus.completedAt,
      spidrCount: focus.spidrCount,
    };
  });
  return rows;
};
/* -------------------------------------------------------------------------------------------------
 * ActionColumn
 * -----------------------------------------------------------------------------------------------*/
const ActionColumn: React.FC<{
  focusID: string;
  detour: boolean;
}> = ({ focusID, detour }) => {
  const { t } = useSecurityJourneyTranslations();

  return (
    <Button
      component={RouterLink}
      to={routes.securityFocuses.focusID.index({
        focusID,
      })}
    >
      {detour
        ? t('securityFocuses.focusesTable.viewDetour')
        : t('securityFocuses.focusesTable.viewFocus')}
    </Button>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Columns
 * -----------------------------------------------------------------------------------------------*/
const AllSecurityFocusesTableColumn = {
  Name: 'name',
  Detour: 'detour',
  State: 'state',
  DateAdded: 'dateAdded',
  CompletedAt: 'completedAt',
  SpidrCount: 'spidrCount',
  Actions: 'actions',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
type AllSecurityFocusesTableColumn =
  (typeof AllSecurityFocusesTableColumn)[keyof typeof AllSecurityFocusesTableColumn];

type ColumnDefinition = GridColDef<SecurityFocusRow>[];

type CustomCellProps = GridRenderCellParams<SecurityFocusRow, unknown> & {};

type GetColumnsOptions = {
  t: TFunction<'translation', undefined>;
};

const getColumns = ({ t }: GetColumnsOptions): ColumnDefinition => {
  const defaultColumns: GridColDef<SecurityFocusRow>[] = [
    {
      field: AllSecurityFocusesTableColumn.Name,
      headerName: t('securityFocuses.focusesTable.focusName'),
      minWidth: 300,
      resizable: true,
      display: 'flex',
    },
    {
      field: AllSecurityFocusesTableColumn.Detour,
      headerName: t('securityFocuses.focusesTable.type'),
      minWidth: 200,
      resizable: true,
      display: 'flex',
      renderCell: ({ row }) => {
        return (
          <Typography>
            {row.detour
              ? t('securityFocuses.focusesTable.detour')
              : t('securityFocuses.focusesTable.securityFocus')}
          </Typography>
        );
      },
      type: 'singleSelect',
      valueOptions: [
        { label: 'Detour', value: true },
        { label: 'Security Focus', value: false },
      ],
      filterOperators: getFilteredGridSelectOperators([
        GridSelectOperator.isAnyOf,
      ]),
    },
    {
      field: AllSecurityFocusesTableColumn.State,
      headerName: t('securityFocuses.focusesTable.status'),
      minWidth: 200,
      resizable: true,
      display: 'flex',
      renderCell: ({ row }) => {
        return (
          <CustomChip
            variant={
              focusStateToAppearance[
                row.state as keyof typeof focusStateToAppearance
              ].variant
            }
            label={t(
              focusStateToAppearance[
                row.state as keyof typeof focusStateToAppearance
              ].text,
            )}
          ></CustomChip>
        );
      },
      type: 'singleSelect',
      valueOptions: createOptions(Object.values(SecurityFocusState)),
      filterOperators: getFilteredGridSelectOperators([
        GridSelectOperator.isAnyOf,
      ]),
    },
    {
      field: AllSecurityFocusesTableColumn.DateAdded,
      headerName: t('securityFocuses.focusesTable.dateAdded'),
      minWidth: 200,
      resizable: true,
      display: 'flex',
      renderCell: ({ row }) => <FormattedDate timestamp={row.dateAdded} />,
    },
    {
      field: AllSecurityFocusesTableColumn.CompletedAt,
      headerName: t('securityFocuses.focusesTable.dateCompleted'),
      minWidth: 200,
      resizable: true,
      display: 'flex',
      renderCell: ({ row }) => <FormattedDate timestamp={row.completedAt} />,
    },
    {
      field: AllSecurityFocusesTableColumn.SpidrCount,
      headerName: t('securityFocuses.focusesTable.totalSpidrs'),
      minWidth: 300,
      resizable: true,
      display: 'flex',
    },
    {
      field: AllSecurityFocusesTableColumn.Actions,
      type: 'actions',
      align: 'right',
      width: 150,
      resizable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return <ActionColumn focusID={row.id} detour={row.detour} />;
      },
    },
  ] satisfies ColumnDefinition;

  return defaultColumns;
};

/* -------------------------------------------------------------------------------------------------
 * AllSecurityFocusesTable
 * -----------------------------------------------------------------------------------------------*/
type AllSecurityFocusesTableProps = {
  /**
   * security focus
   */
  focuses: SecurityFocusWithStateChangeTime[];
};

const AllSecurityFocusesTable: React.FC<AllSecurityFocusesTableProps> = ({
  focuses,
}) => {
  /**
   * get an instance of the translations
   */
  const { t } = useSecurityJourneyTranslations();

  const securityFocuses = parseSecurityFocusesForTable({
    focuses,
  });

  /**
   * compute the columns for the table
   */
  const columns = useMemo(() => {
    return getColumns({
      t,
    });
  }, [t]);

  const slots = {
    toolbar: () => (
      <GridToolbar
        showQuickFilter
        quickFilterProps={{
          placeholder: t(
            'common.blueprintsTable.filters.quickFilter.placeholder',
          ),
        }}
      />
    ),
    noRowsOverlay: () => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Typography variant="body1">
          {t('securityFocuses.focusesTable.emptyMessage')}
        </Typography>
      </Box>
    ),
  };

  return (
    <Stack spacing={1} width="100%" data-testid="security-focus-spidrs-table">
      <Typography variant="h4">{t('common.search')}</Typography>
      <DataGridPro
        rows={securityFocuses}
        columns={columns}
        getRowId={(row: SecurityFocusRow) => row.id}
        slots={slots}
        hideFooter
        disableRowSelectionOnClick
        disableDensitySelector
        disableChildrenFiltering
        getRowHeight={() => 'auto'}
        initialState={{
          pinnedColumns: {
            right: ['actions'],
          },
        }}
      />
    </Stack>
  );
};

export {
  AllSecurityFocusesTable,
  parseSecurityFocusesForTable,
  AllSecurityFocusesTableColumn,
};

export type { AllSecurityFocusesTableProps, SecurityFocusRow, CustomCellProps };
